import { Injectable } from '@angular/core';

import { AccountsService, CommsService, PermissionsService, UserdataService, UserService } from '@services';
import { IObjectStringKeyMap } from '@shared/models';
import { Events } from '@services/events/events.service';
import { FolderDataType, IFolder, ITreeFolder } from '@modules/management/modules/folders/model/folders.interfaces';
import { BaseService } from '@services/abstract-base-service/abstract-base-service.service';

import { cloneDeep, each, filter, flattenDeep, includes, indexOf, intersection, isUndefined, join, last, map, some } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class FoldersDataService extends BaseService {

  private folders: { data: IObjectStringKeyMap<IFolder>; lastRequest: number } = {
    lastRequest: null,
    data: {}
  };

  constructor(
    protected commsService: CommsService,
    private events: Events,
    private userService: UserService,
    private accountsService: AccountsService,
    private permissionsService: PermissionsService,
    private userdataService: UserdataService
  ) {
    super(commsService);
  }

  public refresh() {
    return new Promise((resolve, reject) => {
      this.commsService.sendMessage({
        cmd: 'getFolders',
        includeDisabled: 1,
        lastRequest: this.folders.lastRequest
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          this.updateCache(data);
        }
        resolve(this.folders.data);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  public updateCache(data) {
    this.folders.lastRequest = data.result.timestamp;
    this.folders.data = data.result.folders;
    this.events.publish('ccs:foldersUpdate');
  }

  public addFolder(parameters: any) {
    const requestParameters = {
      cmd: 'addFolder',
      ...parameters
    };

    return this.handleRequest(requestParameters);
  }

  public deleteFolder(parameters: any) {
    const requestParameters = {
      cmd: 'deleteFolder',
      ...parameters
    };

    return this.handleRequest(requestParameters);
  }

  public updateFolder(parameters: any): any {
    const requestParameters = {
      cmd: 'updateFolder',
      ...parameters
    };

    return this.handleRequest(requestParameters);
  }

  public deleteCoreTarget(nfcID: string) {
    return this.handleRequest({ cmd: 'deleteTarget', nfcID });
  }

  public duplicateFolder(folderID: number, targetParentID: number): Promise<any> {
    return this.handleRequest({
      cmd: 'duplicateFolders',
      folderID,
      targetParentID
    });
  }

  public getFolders(types?: string[]) {
    const folders: IObjectStringKeyMap<IFolder> = {};

    each(this.folders.data, (folder, id) => {
      const isActive = folder.disabledAt === 0;

      if (isActive) {
        const filterByType = !!types?.length;
        const isMatched = indexOf(types, folder.type) > -1;

        if ((filterByType && isMatched) || !filterByType) {
          folders[id] = folder;
        }
      }
    });

    return folders;
  }

  public getFolderTreeBy(type: FolderDataType): IObjectStringKeyMap<ITreeFolder> {
    const folders: IObjectStringKeyMap<ITreeFolder> = cloneDeep(this.getFolders([type]));

    each(folders, (folder) => {
      const parentFolder = folders[folder.parentID];

      if (parentFolder) {
        if (!parentFolder.children) {
          parentFolder.children = [];
        }

        parentFolder.children.push(folder);
      }
    });

    each(folders, (folder) => {
      folder.childrenIds = this.extractFolderIds(folder.children);
    });

    return folders;
  }

  public hasIn(parentFolderId: number, childFolderId: number, folderTree: IObjectStringKeyMap<ITreeFolder>): boolean {
    if (parentFolderId === childFolderId) {
      return true;
    } else {
      if (parentFolderId === 0) {
        return !!folderTree[childFolderId];
      } else {
        return includes(folderTree?.[parentFolderId]?.childrenIds, childFolderId);
      }
    }
  }

  public hasInFolders(targetFolder: number, folders: number[], folderTree: IObjectStringKeyMap<ITreeFolder>) {
    return some(folders, (folderID: number) => {
      return this.hasIn(folderID, targetFolder, folderTree) || this.hasIn(targetFolder, folderID, folderTree);
    });
  }

  private extractFolderIds(children: ITreeFolder[]): number[] {
    const folders = flattenDeep(map(children, (folder) => {
      folder.childrenIds = this.extractFolderIds(folder.children || []);
      return [folder, ...folder.childrenIds];
    }));

    return map(folders, (folder) => {
      return (folder as ITreeFolder)?.folderID || folder as number;
    });
  }


  public isActive(folderID: number): boolean {
    const folder = this.getFolderByID(folderID, true);
    if (!folder) {
      return false;
    } else {
      return true;
    }
  }

  public getFolderPath(folderID: number, mustBeActive: boolean = false, separator?: string, translate?: boolean) {
    const path = [];
    let folder = this.getFolderByID(folderID, mustBeActive);
    while (folder) {
      if (translate) {
        path.push(this.userService.translateItem(folder, 'title'));
      } else {
        path.push(folder.title);
      }
      folder = this.getFolderByID(folder.parentID);
    }
    path.reverse();

    if (separator) {
      return join(path, separator);
    } else {
      return path;
    }
  }

  public getFoldersAsync(types: string[]) {
    return new Promise((resolve, reject) => {
      this.commsService.sendMessage({
        cmd: 'getFolders',
        includeDisabled: 1,
        types,
      }, false, false).then(data => {
        if (data && data.reqStatus === 'OK') {
          this.folders.lastRequest = data.result.timestamp;
          this.folders.data = data.result.folders;
          this.events.publish('ccs:foldersUpdate');
        }
        resolve(this.folders.data);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  public getFolderByID(folderID: number, active = true): IFolder {
    const folder = this.folders.data[folderID];

    if (active) {
      return folder?.disabledAt === 0 ? folder : undefined;
    }

    return folder;
  }

  public isHidden(folder: IFolder) {
    return !!(folder?.parentAttributes?.hidden || folder?.attributes?.hidden);
  }

  public getFoldersByType(folderType: FolderDataType, active = true): IFolder[] {
    const filterHandler: IObjectStringKeyMap<number | FolderDataType> = { type: folderType };

    if (active) {
      filterHandler.disabledAt = 0;
    }

    return filter(this.folders.data, filterHandler);
  }

  public getFolderTree(folderType: FolderDataType, folderID: number = 0, folderTree?: IFolder[]): IFolder[] {
    if (isUndefined(folderTree)) {
      folderTree = this.getFoldersByType(folderType);
    }

    let ret = [];

    if (folderID) {
      // we need a subset
      // first, capture THIS folder
      ret = filter(folderTree, { folderID });
      // now see if it has any children
      const children = filter(folderTree, { parentID: folderID });
      if (children.length) {
        // it does!  capture them and any children they have recursively
        each(children, fid => {
          const rollup = this.getFolderTree(folderType, fid.folderID, folderTree);
          each(rollup, item => {
            ret.push(item);
          });
        });
      }
    } else {
      ret = folderTree;
    }
    return ret;
  }

  public canAccessFolder(folder: IFolder): boolean {
    // No folder or no restrictions = allow access
    if (!folder || !folder.attributes) {
      return true;
    }

    const attributes = folder.attributes || {};
    const toArray = (val: number | number[] | string | string[] | undefined): any[] => {
      if (!val && val !== 0) {
        return [];
      }
      return Array.isArray(val) ? val : [val];
    };

    // Check if there are ANY access restrictions set
    const hasRestrictions =
      toArray(attributes?.access_certifications).length > 0 ||
      toArray(attributes?.access_locations).length > 0 ||
      toArray(attributes?.access_permissions).length > 0 ||
      toArray(attributes?.access_roles).length > 0 ||
      toArray(attributes?.access_shifts).length > 0 ||
      toArray(attributes?.access_teams).length > 0;

    // If no restrictions are set, everyone has access
    if (!hasRestrictions) {
      return true;
    }

    const userID = +this.userdataService.userID;
    const account = this.accountsService.getAccount(userID);

    // Staff/Corvex users always have access
    if (this.permissionsService.canView('corvex')) {
      return true;
    }

    // Owner always has access
    // Note: folder.creator should match the actual user, not kiosk account
    if (folder.creator === userID) {
      return true;
    }

    let hasAccess = true;

    // Check certifications
    if (hasRestrictions) {
      if (hasAccess && attributes?.access_certifications) {
        const certArray = toArray(attributes.access_certifications);
        if (certArray.length > 0) {
          hasAccess = !!intersection(certArray, this.userdataService.certifications || []).length;
        }
      }

      if (hasAccess && attributes?.access_locations) {
        const locArray = toArray(attributes.access_locations);
        if (locArray.length > 0) {
          const currentLocationId = (this.userdataService as any).locationID;
          hasAccess = includes(locArray, currentLocationId);
        }
      }

      if (hasAccess && attributes?.access_permissions) {
        const permArray = toArray(attributes.access_permissions);
        if (permArray.length > 0) {
          hasAccess = some(permArray, (permission) => this.permissionsService.canView(permission));
        }
      }

      if (hasAccess && attributes?.access_roles) {
        const roleArray = toArray(attributes.access_roles);
        if (roleArray.length > 0) {
          hasAccess = !!intersection(roleArray, this.userdataService.roles || []).length;
        }
      }

      if (hasAccess && attributes?.access_shifts) {
        const shiftArray = toArray(attributes.access_shifts);
        if (shiftArray.length > 0) {
          hasAccess = !!intersection(shiftArray, account?.shifts || []).length;
        }
      }

      if (hasAccess && attributes?.access_teams) {
        const teamArray = toArray(attributes.access_teams);
        if (teamArray.length > 0) {
          hasAccess = !!intersection(teamArray, account?.groups || []).length;
        }
      }
    }

    if (hasAccess && folder.parentID) {
      const parentFolder = this.getFolderByID(folder.parentID);
      if (parentFolder) {
        hasAccess = this.canAccessFolder(parentFolder);
      }
    }

    return hasAccess;
  }
}
